// Subscription utility functions for managing user subscriptions
import { API_URL } from '../config';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

export const handleUpgrade = async (user, auth, setUpgradeLoading) => {
  if (!user) return;
  setUpgradeLoading(true);
  try {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`${API_URL}/api/subscription/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({})
    });
    const data = await res.json();
    if (data.url) {
      // Open Stripe Checkout in a new tab
      window.open(data.url, '_blank');
    } else {
      alert('Could not start checkout. No checkout URL returned.');
      console.error('Checkout response:', data);
    }
  } catch (err) {
    alert('Error starting checkout: ' + err.message);
  } finally {
    setUpgradeLoading(false);
  }
};

/**
 * Fetch subscription status directly from Firestore (instant, no backend required)
 * Optionally syncs with backend in background for validation
 */
export const fetchSubscriptionStatus = async (auth, setUserSubscription, syncWithBackend = true) => {
  console.log('🔍 fetchSubscriptionStatus called, auth.currentUser:', auth.currentUser?.uid);
  if (!auth.currentUser) {
    console.log('⚠️ No user found, skipping subscription fetch');
    return;
  }

  const userId = auth.currentUser.uid;

  try {
    // Read directly from Firestore - instant, no backend needed
    console.log('📖 Reading subscription from Firestore...');
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.log('⚠️ User document not found, defaulting to free tier');
      setUserSubscription({ tier: 'free', status: 'active' });
      return;
    }

    const userData = userSnap.data();
    const subscription = userData.subscription || {};

    // Format subscription data to match backend response format
    const subscriptionData = {
      tier: subscription.tier || 'free',
      status: subscription.status || 'active',
      stripeCustomerId: subscription.stripeCustomerId || null,
      stripeSubscriptionId: subscription.stripeSubscriptionId || null,
      renewalDate: subscription.renewalDate || null,
      createdAt: subscription.createdAt || null
    };

    console.log('✅ Subscription status from Firestore:', subscriptionData);
    setUserSubscription(subscriptionData);

    // Optionally sync with backend in background to ensure data is up-to-date
    // This is non-blocking and won't delay the UI
    if (syncWithBackend) {
      syncSubscriptionWithBackend(auth, setUserSubscription).catch(error => {
        console.log('⚠️ Background sync with backend failed (non-critical):', error.message);
        // Don't update state on sync failure - Firestore data is already set
      });
    }

  } catch (error) {
    console.error('❌ Error reading subscription from Firestore:', error);
    // Fallback to free tier on error
    setUserSubscription({ tier: 'free', status: 'active' });
    
    // If Firestore fails, try backend as fallback
    if (syncWithBackend) {
      console.log('🔄 Falling back to backend for subscription status...');
      await fetchSubscriptionStatusFromBackend(auth, setUserSubscription);
    }
  }
};

/**
 * Sync subscription status with backend (non-blocking, runs in background)
 */
async function syncSubscriptionWithBackend(auth, setUserSubscription) {
  try {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`${API_URL}/api/subscription/status`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (res.ok) {
      const data = await res.json();
      console.log('🔄 Backend sync successful, updating subscription:', data);
      setUserSubscription(data);
    } else {
      console.log('⚠️ Backend sync returned non-OK status:', res.status);
    }
  } catch (error) {
    // Silently fail - this is just a background sync
    throw error;
  }
}

/**
 * Fallback: Fetch subscription status from backend (with retry logic)
 * Used when Firestore read fails
 */
async function fetchSubscriptionStatusFromBackend(auth, setUserSubscription) {
  const maxRetries = 12; // Retry for up to 2 minutes
  const retryDelay = 10000; // 10 seconds between retries
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const token = await auth.currentUser.getIdToken();
      console.log(`🔑 Fetching from backend... (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      const res = await fetch(`${API_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('✅ Subscription status from backend:', data);
        setUserSubscription(data);
        return;
      }
      
      // If 503 (service unavailable), retry
      if (res.status === 503 && attempt < maxRetries) {
        console.log(`⏳ Backend spinning up (503), retrying in ${retryDelay/1000}s...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }
      
      // Other errors - set default
      console.warn('⚠️ Backend returned error:', res.status);
      setUserSubscription({ tier: 'free', status: 'active' });
      return;
      
    } catch (error) {
      // Network error or timeout - retry if we have attempts left
      if (attempt < maxRetries) {
        console.log(`⏳ Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${retryDelay/1000}s...`, error.message);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }
      
      // Out of retries - set default
      console.error('❌ Error fetching subscription status after all retries:', error);
      setUserSubscription({ tier: 'free', status: 'active' });
      return;
    }
  }
}

export const handleCancelSubscription = async (auth, setUserSubscription, setManageSubscriptionModalOpen, setCancelLoading) => {
  if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
    return;
  }
  
  setCancelLoading(true);
  try {
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`${API_URL}/api/subscription/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    const data = await res.json();
    
    if (data.success) {
      alert('Subscription canceled. You will retain premium access until the end of your billing period.');
      await fetchSubscriptionStatus(auth, setUserSubscription); // Refresh subscription status
      setManageSubscriptionModalOpen(false);
    } else {
      alert('Error canceling subscription: ' + (data.error || 'Unknown error'));
    }
  } catch (error) {
    alert('Error canceling subscription: ' + error.message);
  } finally {
    setCancelLoading(false);
  }
};
